import { useEffect, useMemo, useRef, useState } from 'react'
import { bettingApi } from '../../api'
import { ErrorState, errorMessage } from '../ErrorState'
import type {
  DiscoveryCandidate,
  DiscoveryReport,
  BettingDiscoverySource,
  ImportDecision,
  ImportEntry,
} from '../../types'
import { useShell } from '../../shell/ShellContext'
import { navigate } from '../../shell/router'
import { Block, PageHeader, StatusPill } from '../../ui/ui'
import { useI18n } from '../../i18n'
import type { MessageKey, TFunction } from '../../i18n'
import { countryLabel, useBettingVocabulary } from './bettingShared'

/**
 * Recherche de sites de paris non encore suivis.
 *
 * Deux sources, de coûts très différents : la recherche web consomme le quota
 * mensuel SerpAPI, une requête par modèle envoyé, tandis que les publicités
 * relisent les annonceurs déjà collectés et ne coûtent rien. L'écran dit ce
 * qu'une passe va dépenser avant de la lancer, et ce qu'elle a dépensé après.
 *
 * Rien n'est écrit en base par la recherche : elle rend des candidats. C'est
 * l'import, en deux temps — plan puis confirmation — qui crée les services.
 */

const SOURCE_OPTIONS: Array<{ value: BettingDiscoverySource; labelKey: MessageKey; costKey: MessageKey }> = [
  { value: 'SEARCH', labelKey: 'bet.sourceSearch', costKey: 'bet.sourceSearchCost' },
  { value: 'ADS', labelKey: 'bet.sourceAds', costKey: 'bet.sourceAdsCost' },
]

const ACTION_TONES: Record<ImportDecision['action'], 'ok' | 'warn' | 'crit' | 'info'> = {
  CREATE: 'ok',
  ADD_MIRROR: 'info',
  SKIP: 'warn',
  REJECT: 'crit',
}

/**
 * Motifs de rejet proposés, pour que le plus courant se choisisse d'un clic.
 *
 * Le motif reste facultatif : une passe écarte des dizaines de domaines d'un
 * coup, et l'exiger ferait saisir des motifs de complaisance. Le rendement du
 * vocabulaire dit combien de décisions en portent un, pour que le silence se
 * voie au lieu d'être comblé.
 */
const MOTIFS_REJET: MessageKey[] = [
  'bet.reasonComparator',
  'bet.reasonPress',
  'bet.reasonNotBetting',
  'bet.reasonWrongCountry',
  'bet.reasonDuplicate',
  'bet.reasonUnreachable',
]

/**
 * Trace de provenance, conservée sur le service : d'où il sort, et quand.
 *
 * Elle est écrite dans la langue de l'agent qui a mené la passe : c'est une
 * note de dossier, pas un libellé d'interface, et la retraduire plus tard
 * réécrirait ce qu'il a consigné.
 */
function provenance(t: TFunction, c: DiscoveryCandidate, date: string): string {
  const lignes = [t('bet.provenanceFound', { date })]
  if (c.queries.length > 0) lignes.push(t('bet.provenanceQueries', { queries: c.queries.join(' ; ') }))
  if (c.advertisers.length > 0) lignes.push(t('bet.provenanceAdvertisers', { advertisers: c.advertisers.join(' ; ') }))
  if (c.evidence) lignes.push(t('bet.provenanceExcerpt', { excerpt: c.evidence }))
  return lignes.join('\n')
}

export function BettingDiscovery({ canManage }: { canManage: boolean }) {
  const { t, fmt } = useI18n()
  const { country } = useShell()
  const vocab = useBettingVocabulary()

  const [pays, setPays] = useState(country || '')
  const [sources, setSources] = useState<BettingDiscoverySource[]>(['SEARCH', 'ADS'])
  const [budget, setBudget] = useState('')
  const [enCours, setEnCours] = useState(false)
  const [rapport, setRapport] = useState<DiscoveryReport | null>(null)
  const [retenus, setRetenus] = useState<Set<string>>(new Set())
  const [plan, setPlan] = useState<ImportDecision[] | null>(null)
  const planRef = useRef<HTMLDivElement>(null)
  const [bilan, setBilan] = useState<ImportDecision[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  /** Motif de rejet, par domaine écarté. */
  const [motifs, setMotifs] = useState<Record<string, string>>({})
  const [jugements, setJugements] = useState<string | null>(null)
  // Une saisie incomplète n'est pas une panne : `ErrorState` annonce
  // « Données indisponibles », ce qui ferait croire à un serveur muet.
  const [consigne, setConsigne] = useState<string | null>(null)

  const candidats = rapport?.candidates ?? []
  const entrees = useMemo(
    () =>
      candidats
        .filter((c) => retenus.has(c.domain))
        .map<ImportEntry>((c) => ({
          name: c.name,
          country: c.country,
          mainUrl: c.mainUrl,
          notes: provenance(t, c, fmt.date(new Date())),
        })),
    [candidats, retenus, t, fmt],
  )

  const basculer = (domain: string) =>
    setRetenus((s) => {
      const copie = new Set(s)
      if (copie.has(domain)) copie.delete(domain)
      else copie.add(domain)
      return copie
    })

  const lancer = async () => {
    if (!pays) {
      setConsigne(t('bet.pickCountryFirst'))
      return
    }
    setConsigne(null)
    setError(null)
    setEnCours(true)
    setRapport(null)
    setPlan(null)
    setBilan(null)
    setMotifs({})
    setJugements(null)
    try {
      const r = await bettingApi.discover({
        country: pays,
        sources,
        maxQueries: budget ? Number(budget) : undefined,
      })
      setRapport(r)
      // Les comparateurs sont conservés mais décochés : ce sont rarement des opérateurs.
      setRetenus(new Set(r.candidates.filter((c) => !c.likelyReviewSite).map((c) => c.domain)))
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setEnCours(false)
    }
  }

  /**
   * Amene le plan d'import dans le champ de vision des qu'il apparait.
   *
   * Le bloc se rend sous le tableau des candidats, qui fait souvent plusieurs
   * ecrans de haut : le clic sur « Preparer l'import » restait alors sans
   * effet visible, et l'agent pouvait le croire sans effet tout court.
   *
   * L'effet porte sur `plan` et non sur le gestionnaire du bouton : au moment
   * du clic la requete d'import n'a pas encore repondu et le bloc n'est pas
   * monte. Il ne se declenche pas a l'annulation, ou `plan` repasse a null.
   *
   * Le defilement seul ne dit rien a un lecteur d'ecran, d'ou le focus porte
   * sur le bloc ; `preventScroll` evite de doubler le mouvement.
   */
  useEffect(() => {
    const bloc = planRef.current
    if (!plan || !bloc) return
    const sobre = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    bloc.scrollIntoView({ behavior: sobre ? 'auto' : 'smooth', block: 'start' })
    bloc.focus({ preventScroll: true })
  }, [plan])

  const preparer = async () => {
    setError(null)
    try {
      const r = await bettingApi.importServices(entrees, false)
      setPlan(r.decisions)
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  /**
   * Consigne le jugement porté sur chaque candidat de la passe.
   *
   * Les décochés comptent autant que les retenus : décocher un domaine, c'est
   * dire que les termes qui l'ont remonté produisent du bruit. Sans cet appel,
   * cette appréciation disparaît avec la page.
   *
   * Un candidat retenu est consigné comme retenu même si l'import le range en
   * « déjà connu » : l'agent a bien jugé que c'était un opérateur, et c'est son
   * jugement que le vocabulaire doit porter au crédit du terme.
   */
  const consignerJugements = async (): Promise<string | null> => {
    if (!rapport || candidats.length === 0) return null
    const decisions = candidats.map((c) =>
      retenus.has(c.domain)
        ? ({ domain: c.domain, status: 'IMPORTED' } as const)
        : ({ domain: c.domain, status: 'REJECTED', reason: motifs[c.domain] || null } as const),
    )
    const r = await bettingApi.decideDiscoveryCandidates(rapport.country, decisions)
    const ecartes = decisions.filter((d) => d.status === 'REJECTED').length
    const manquants = r.unknown.length
      ? t('bet.judgementsMissing', { n: r.unknown.length, domains: r.unknown.join(', ') })
      : ''
    return t('bet.judgementsSaved', {
      updated: r.updated,
      kept: decisions.length - ecartes,
      dropped: ecartes,
      missing: manquants,
    })
  }

  const enregistrerSeulement = async () => {
    setError(null)
    setJugements(null)
    try {
      setJugements(await consignerJugements())
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  const confirmer = async () => {
    setError(null)
    setJugements(null)
    try {
      const r = await bettingApi.importServices(entrees, true)
      setBilan(r.decisions)
      setPlan(null)
      // Après l'import : un échec de consignation ne doit pas faire croire que
      // l'import a échoué, puisqu'il est déjà fait.
      try {
        setJugements(await consignerJugements())
      } catch (err) {
        setJugements(t('bet.importedButNotJudged', { error: errorMessage(err) }))
      }
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  const coutAnnonce = sources.includes('SEARCH')
    ? budget
      ? t('bet.costBudget', { n: Number(budget) })
      : t('bet.costPerPattern')
    : t('bet.costNone')

  return (
    <>
      <PageHeader
        title={t('bet.discoveryTitle')}
        description={t('bet.discoveryDesc')}
        actions={
          <button type="button" className="btn btn-secondary" onClick={() => navigate('betting/referentials')}>
            {t('bet.vocabularyLink')}
          </button>
        }
      />

      {!canManage && (
        <p className="bet-notice" role="status">{t('bet.discoveryManagerOnly')}</p>
      )}

      {consigne && (
        <p className="bet-notice" role="status">
          {consigne}
        </p>
      )}
      {error && <ErrorState message={error} compact />}

      <Block title={t('bet.runPass')} subtitle={t('bet.passCost', { cost: coutAnnonce })}>
        <div className="bet-discovery-form">
          <label>
            {t('common.country')}
            <select value={pays} onChange={(e) => setPays(e.target.value)} disabled={!canManage || !!country}>
              <option value="">{t('ads.pick')}…</option>
              {(vocab?.countries ?? []).map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t('bet.queryBudget')}
            <input
              type="number"
              min={1}
              max={20}
              value={budget}
              placeholder={t('bet.allPatterns')}
              onChange={(e) => setBudget(e.target.value)}
              disabled={!canManage || !sources.includes('SEARCH')}
            />
          </label>
          <fieldset className="bet-discovery-sources">
            <legend>{t('bet.sources')}</legend>
            {SOURCE_OPTIONS.map((s) => (
              <label key={s.value}>
                <input
                  type="checkbox"
                  checked={sources.includes(s.value)}
                  disabled={!canManage}
                  onChange={(e) =>
                    setSources((cur) =>
                      e.target.checked ? [...cur, s.value] : cur.filter((v) => v !== s.value),
                    )
                  }
                />
                {t(s.labelKey)} <span className="bet-muted">— {t(s.costKey)}</span>
              </label>
            ))}
          </fieldset>
          <button
            type="button"
            className="btn btn-primary"
            onClick={lancer}
            disabled={!canManage || enCours || sources.length === 0}
          >
            {enCours ? t('bet.searching') : t('bet.runSearch')}
          </button>
        </div>
        {country && (
          <p className="bet-hint">{t('bet.countryFollowsHeader')}</p>
        )}
      </Block>

      {rapport && (
        <Block
          title={t('bet.discoveryResultFor', { country: countryLabel(vocab, rapport.country, t) })}
          subtitle={t('bet.discoveryResultSub', {
            queries: rapport.queriesRun.length,
            results: rapport.resultsSeen,
            advertisers: rapport.advertisersScanned,
            known: rapport.knownDomains,
          })}
          action={
            candidats.length > 0 && canManage ? (
              <>
                {/* Une passe dont rien n'est retenu est le signal le plus net qu'un
                    terme produit du bruit : elle doit pouvoir se consigner seule. */}
                <button type="button" className="btn btn-secondary" onClick={enregistrerSeulement}>
                  {t('bet.saveJudgements')}
                </button>{' '}
                <button type="button" className="btn btn-primary" onClick={preparer} disabled={entrees.length === 0}>
                  {t('bet.prepareImport', { n: entrees.length })}
                </button>
              </>
            ) : undefined
          }
        >
          {rapport.warning && (
            <p className="bet-notice" role="status">
              {rapport.warning}
            </p>
          )}
          {rapport.queriesRun.length > 0 && (
            <p className="bet-hint">{t('bet.queriesSent', { queries: rapport.queriesRun.join(' · ') })}</p>
          )}
          {jugements && (
            <p className="bet-notice" role="status">
              {jugements}
            </p>
          )}
          {candidats.length > 0 && (
            <p className="bet-hint">{t('bet.judgementsHint')}</p>
          )}
          {candidats.length === 0 ? (
            <p className="bet-muted">{t('bet.noCandidate')}</p>
          ) : (
            <div className="table-scroll">
              <table className="bet-table">
                <thead>
                  <tr>
                    <th scope="col">
                      <span className="sr-only">{t('bet.colKeep')}</span>
                    </th>
                    <th scope="col">{t('common.domain')}</th>
                    <th scope="col">{t('bet.colProposedName')}</th>
                    <th scope="col">{t('bet.colWhySurfaced')}</th>
                    {canManage && <th scope="col">{t('bet.colRejectReason')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {candidats.map((c) => (
                    <tr key={c.domain}>
                      <td>
                        <input
                          type="checkbox"
                          checked={retenus.has(c.domain)}
                          disabled={!canManage}
                          onChange={() => basculer(c.domain)}
                          aria-label={t('bet.keepDomain', { domain: c.domain })}
                        />
                      </td>
                      <td className="bet-mono">
                        <a href={c.mainUrl} target="_blank" rel="noreferrer noopener">
                          {c.domain}
                        </a>
                        {c.likelyReviewSite && (
                          <>
                            {' '}
                            <StatusPill tone="warn">{t('bet.likelyReviewSite')}</StatusPill>
                          </>
                        )}
                      </td>
                      <td>{c.name}</td>
                      <td>
                        <p className="bet-discovery-evidence">{c.evidence || <span className="bet-muted">—</span>}</p>
                        {c.queries.length > 0 && (
                          <p className="bet-muted">{t('bet.candQueries', { queries: c.queries.join(' · ') })}</p>
                        )}
                        {c.advertisers.length > 0 && (
                          <p className="bet-muted">{t('bet.candAdvertisers', { advertisers: c.advertisers.join(' · ') })}</p>
                        )}
                        {c.queryTerms.length > 0 && (
                          <p className="bet-muted">{t('bet.candTerms', { terms: c.queryTerms.join(' · ') })}</p>
                        )}
                      </td>
                      {canManage && (
                        <td>
                          <select
                            value={motifs[c.domain] ?? ''}
                            disabled={retenus.has(c.domain)}
                            aria-label={t('bet.rejectReasonAria', { domain: c.domain })}
                            onChange={(e) => setMotifs((m) => ({ ...m, [c.domain]: e.target.value }))}
                          >
                            <option value="">{t('bet.noReason')}</option>
                            {/* La valeur consignée est le motif tel qu'il s'est
                                affiché à l'agent : c'est sa décision, pas une clé. */}
                            {MOTIFS_REJET.map((key) => (
                              <option key={key} value={t(key)}>
                                {t(key)}
                              </option>
                            ))}
                          </select>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Block>
      )}

      {plan && (
        <div ref={planRef} tabIndex={-1}>
          <Block
            title={t('bet.importPlanTitle')}
            subtitle={t('bet.importPlanSub')}
            action={
              <>
                <button type="button" className="btn btn-secondary" onClick={() => setPlan(null)}>
                  {t('common.cancel')}
                </button>{' '}
                <button type="button" className="btn btn-primary" onClick={confirmer}>
                  {t('bet.confirmImport')}
                </button>
              </>
            }
          >
            <DecisionTable decisions={plan} />
          </Block>
        </div>
      )}

      {bilan && (
        <Block
          title={t('bet.importDoneTitle')}
          action={
            <button type="button" className="btn btn-primary" onClick={() => navigate('betting/services')}>
              {t('bet.seeServices')}
            </button>
          }
        >
          <DecisionTable decisions={bilan} />
        </Block>
      )}
    </>
  )
}

function DecisionTable({ decisions }: { decisions: ImportDecision[] }) {
  const { t } = useI18n()
  return (
    <div className="table-scroll">
      <table className="bet-table">
        <thead>
          <tr>
            <th scope="col">{t('common.actions')}</th>
            <th scope="col">{t('ads.filterSite')}</th>
            <th scope="col">{t('common.reason')}</th>
          </tr>
        </thead>
        <tbody>
          {decisions.map((d, i) => (
              <tr key={`${d.entry.mainUrl}-${i}`}>
                <td>
                  <StatusPill tone={ACTION_TONES[d.action]}>
                    {t(`bet.decisionAction.${d.action}` as const)}
                  </StatusPill>
                </td>
                <td className="bet-mono">{d.entry.mainUrl}</td>
                <td>
                  {d.reason}
                  {d.serviceName && <span className="bet-muted"> — {d.serviceName}</span>}
                </td>
              </tr>
          ))}
          {decisions.length === 0 && (
            <tr>
              <td colSpan={3} className="bet-muted">
                {t('bet.noEntryKept')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
